import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { today } from "../utils/dateUtils";
import LeaveHistory from "../components/LeaveHistory";
import AttendanceTable from "../components/AttendanceTable";
import LeaveModal from "../components/LeaveModal";
import CheckInCheckOutCard from "../components/CheckInCheckOutCard";
import ExpenseModal from "../components/ExpenseModal";
import { SalaryInfoModal, SalarySlipModal } from "../components/SalarySlipModal";
import { SERVER_URL } from "../config";
import Sidebar from "../components/Sidebar";
import {
  Bars3Icon,
  ClockIcon,
  CalendarDaysIcon,
  TableCellsIcon,
  BanknotesIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";

const toDateStr = (date) => date.toISOString().split("T")[0];

// ── Tab definitions ────────────────────────────────────────────────────────────
const TABS = [
  { key: "attendance", label: "Attendance",    icon: TableCellsIcon },
  { key: "leaves",     label: "Leave History", icon: CalendarDaysIcon },
];

/* ════════════════════════════════════════════════════════════════════════════ */
const AttendancePage = () => {
  useNavigate();
  const employee = JSON.parse(localStorage.getItem("employee"));
  if (!employee) { window.location.href = "/login"; return null; }

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]                     = useState("attendance");
  const [allRecords, setAllRecords]                   = useState([]);
  const [isLoading, setIsLoading]                     = useState(false);
  const [fetchError, setFetchError]                   = useState("");
  const [leaveHistoryData, setLeaveHistoryData]       = useState([]);
  const [leaveLoading, setLeaveLoading]               = useState(false);
  const [showLeaveModal, setShowLeaveModal]           = useState(false);
  const [isLeaveSubmitting, setIsLeaveSubmitting]     = useState(false);
  const [leaveStatus, setLeaveStatus]                 = useState("");
  const [leaveData, setLeaveData]                     = useState("");
  const [isSidebarOpen, setIsSidebarOpen]             = useState(false);
  const [showExpenseModal, setShowExpenseModal]       = useState(false);
  const [showSalaryInfoModal, setShowSalaryInfoModal] = useState(false);
  const [showSalarySlipModal, setShowSalarySlipModal] = useState(false);
  const [selectedSlipId, setSelectedSlipId]           = useState(null);

  // ── isOnLeaveToday ──────────────────────────────────────────────────────────
  const isOnLeaveToday = useMemo(() => {
    const todayISO = new Date().toISOString().slice(0, 10);
    return (leaveHistoryData ?? []).some((leave) => {
      const from   = String(leave.start_date ?? "").slice(0, 10);
      const to     = String(leave.end_date   ?? "").slice(0, 10);
      const status = String(leave.status     ?? "").toLowerCase();
      return from && to && todayISO >= from && todayISO <= to &&
             (status === "pending" || status === "approved");
    });
  }, [leaveHistoryData]);

  // ── Fetch full attendance history (current year) ───────────────────────────
  const fetchAttendanceHistory = useCallback(async () => {
    setIsLoading(true);
    setFetchError("");
    try {
      const yearStart = `${new Date().getFullYear()}-01-01`;
      const res = await axios.post(`${SERVER_URL}/DataFetch/attendanceHistory.php`, {
        employee_id:   employee.employee_id   ?? null,
        employee_name: employee.employee_name ?? null,
        start_date:    yearStart,
        end_date:      toDateStr(new Date()),
      });
      if (res.data.status === true) {
        setAllRecords(res.data.records ?? []);
      } else {
        setFetchError(res.data.message ?? "Failed to fetch attendance.");
      }
    } catch (err) {
      console.error("Error fetching attendance:", err);
      setFetchError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [employee.employee_id, employee.employee_name]);

  // ── Fetch leave history for selected month ─────────────────────────────────
  const fetchLeaveHistory = useCallback(async (month, year) => {
    setLeaveLoading(true);
    try {
      const m   = month ?? new Date().getMonth() + 1;
      const y   = year  ?? new Date().getFullYear();
      const pad = (n) => String(n).padStart(2, "0");
      const startDate = `${y}-${pad(m)}-01`;
      const lastDay   = new Date(y, m, 0).getDate();
      const endDate   = `${y}-${pad(m)}-${lastDay}`;
      const res = await axios.post(
        `${SERVER_URL}/Rahul/fetchEmployeeWiseLeaveHistory.php`,
        { employeeId: employee.employee_id, startDate, endDate }
      );
      setLeaveHistoryData(res.data.status === "success" ? res.data.leaves : []);
    } catch (err) {
      console.error("Error fetching leave history:", err);
      setLeaveHistoryData([]);
    } finally {
      setLeaveLoading(false);
    }
  }, [employee.employee_id]);

  useEffect(() => {
    fetchAttendanceHistory();
    fetchLeaveHistory(new Date().getMonth() + 1, new Date().getFullYear());
  }, [fetchAttendanceHistory, fetchLeaveHistory]);

  // ── Leave submit ────────────────────────────────────────────────────────────
  const submitLeaveApplication = async () => {
    setIsLeaveSubmitting(true);
    setLeaveStatus("");
    setTimeout(() => {
      setLeaveStatus("Leave application submitted successfully!");
      setTimeout(() => {
        setShowLeaveModal(false);
        setIsLeaveSubmitting(false);
        fetchLeaveHistory();
      }, 1000);
    }, 1200);
  };

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; }
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      {/* Subtle top accent line */}
      <div
        className="fixed top-0 left-0 right-0 h-0.5 z-50"
        style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
      />

      {/* PAGE — same #f0f4f8 background as AdminAttendanceOverview */}
      <div className="flex h-screen overflow-hidden font-[Inter]" style={{ background: "#f0f4f8" }}>

        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 flex flex-col overflow-hidden">

          {/* ── STICKY HEADER ── */}
          <div
            className="sticky top-0 z-20 border-b border-slate-200"
            style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}
          >
            <div className="px-7 py-3.5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                >
                  <Bars3Icon className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2.5">
                  {/* Blue accent bar — same as AdminAttendanceOverview */}
                  <div
                    className="w-1 h-6 rounded-full"
                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
                  />
                  <ClockIcon className="w-5 h-5 text-blue-700" />
                  <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                    Attendance Dashboard
                  </h1>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowExpenseModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                >
                  <BanknotesIcon className="w-4 h-4" />
                  Expense
                </button>
                <button
                  onClick={() => setShowSalaryInfoModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  <CreditCardIcon className="w-4 h-4" />
                  Pay Slip
                </button>
              </div>
            </div>
          </div>

          {/* ── SCROLLABLE CONTENT ── */}
          <div className="flex-1 overflow-y-auto att-scrollbar">
            <div className="p-5 max-w-7xl mx-auto flex flex-col gap-4">

              {/* Check-in card */}
              <CheckInCheckOutCard
                today={today}
                error=""
                isLoading={isLoading}
                setShowLeaveModal={setShowLeaveModal}
                employeeId={employee.employee_id}
                employeeName={employee.employee_name}
                SERVER_URL={SERVER_URL}
                isOnLeaveToday={isOnLeaveToday}
              />

              {/* ── Tabs + Table section ── */}
              <div
                className="rounded-xl overflow-hidden bg-white border border-slate-200"
                style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}
              >
                {/* Tab bar */}
                <div className="flex border-b border-slate-200" style={{ background: "#f8fafc" }}>
                  {TABS.map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`flex items-center gap-2 px-6 py-3.5 text-sm font-semibold transition-all duration-150 border-b-2 ${
                        activeTab === key
                          ? "border-blue-700 text-blue-700 bg-white"
                          : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                <div className="p-5 bg-white">
                  {activeTab === "attendance" && (
                    <AttendanceTable
                      data={allRecords}
                      isLoading={isLoading}
                      error={fetchError}
                      fetchAttendance={fetchAttendanceHistory}
                    />
                  )}
                  {activeTab === "leaves" && (
                    <LeaveHistory
                      leaveData={leaveHistoryData}
                      isLoading={leaveLoading}
                      onMonthChange={fetchLeaveHistory}
                    />
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── Modals ── */}
        {showLeaveModal && (
          <LeaveModal
            leaveData={leaveData}
            setLeaveData={setLeaveData}
            today={today}
            submitLeaveApplication={submitLeaveApplication}
            isLoading={isLeaveSubmitting}
            leaveStatus={leaveStatus}
            setShowLeaveModal={setShowLeaveModal}
          />
        )}
        {showExpenseModal && (
          <ExpenseModal isOpen={showExpenseModal} onClose={() => setShowExpenseModal(false)} employee={employee} />
        )}
        {showSalaryInfoModal && (
          <SalaryInfoModal
            isOpen={showSalaryInfoModal}
            onClose={() => setShowSalaryInfoModal(false)}
            employee={employee}
            onViewSlip={(id) => { setSelectedSlipId(id); setShowSalarySlipModal(true); }}
          />
        )}
        {showSalarySlipModal && selectedSlipId && (
          <SalarySlipModal slipId={selectedSlipId} onClose={() => setShowSalarySlipModal(false)} />
        )}
      </div>
    </>
  );
};

export default AttendancePage;